import { currentOrderedPaidProductLevels } from 'pricing-pages-ui-components/constants/ProductLevel';
import { createToObject } from 'pricing-pages-ui-components/utils/queryParamUtils';
import { useHistory, useLocation } from 'quartz-routing/react-router';
import { useCallback, useEffect, useMemo } from 'react';
import { useCurrency } from '../../context/CurrencyContext';
import { Screens } from '../../types/PublicNavItem';
import { TRACKING_SOURCES, trackClickedLink } from '../../utils/tracking';
import { useHsocPublicTierOptions } from '../hsoc-pricing/useHsocPublicTierOptions';
import { resolveFscTerm, withPrimaryProduct } from './fscUrlCart';
import { getFscTierOptions } from './publicFscConfigureHelpers';
import { usePublicFscCompareSeats } from './usePublicFscCompareSeats';
export function usePublicFscConfigurePage() {
  var _tierOptions$;
  const history = useHistory();
  const {
    search
  } = useLocation();
  const {
    displayedCurrencyCode,
    isLoading: isCurrencyLoading
  } = useCurrency();
  const {
    productsByTier,
    products,
    status: tierOptionsStatus,
    browserLocale,
    preferredCountryCode
  } = useHsocPublicTierOptions(displayedCurrencyCode, {
    skip: isCurrencyLoading
  });
  const catalog = useMemo(() => productsByTier && products ? {
    productsByTier,
    products
  } : undefined, [productsByTier, products]);
  const primaryProduct = new URLSearchParams(search).get('primaryProduct');
  const selectedTier = primaryProduct ? currentOrderedPaidProductLevels.find(tier => {
    var _productsByTier$tier;
    return (productsByTier === null || productsByTier === void 0 || (_productsByTier$tier = productsByTier[tier]) === null || _productsByTier$tier === void 0 ? void 0 : _productsByTier$tier.productApiName) === primaryProduct;
  }) : undefined;
  const selectedTierOption = selectedTier ? productsByTier === null || productsByTier === void 0 ? void 0 : productsByTier[selectedTier] : undefined;
  const term = resolveFscTerm(selectedTierOption === null || selectedTierOption === void 0 ? void 0 : selectedTierOption.pricing.purchaseOptions, search);
  const tierOptions = useMemo(() => getFscTierOptions(productsByTier, term), [productsByTier, term]);
  const selectedOption = tierOptions.find(option => option.apiName === primaryProduct);
  const defaultApiName = (_tierOptions$ = tierOptions[0]) === null || _tierOptions$ === void 0 ? void 0 : _tierOptions$.apiName;
  const compareSeats = usePublicFscCompareSeats({
    tierOption: selectedTierOption,
    products,
    primaryPricePerSeat: selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.pricePerSeat,
    currencyCode: displayedCurrencyCode
  });
  useEffect(() => {
    if (tierOptionsStatus !== 'ready' || selectedTier || !defaultApiName) {
      return;
    }
    history.replace({
      search: withPrimaryProduct(search, defaultApiName)
    });
  }, [tierOptionsStatus, selectedTier, defaultApiName, history, search]);
  const backToPricing = useMemo(() => createToObject('/'), []);
  const backToPricingHref = history.createHref(backToPricing);
  const onBackToPricing = useCallback(event => {
    event.preventDefault();
    trackClickedLink({
      type: 'internal',
      url: backToPricingHref,
      name: 'fsc-configure-back-to-pricing',
      trackingSource: TRACKING_SOURCES.FSC_CONFIGURE,
      screen: Screens.FSC_CONFIGURE
    });
    history.push(backToPricing);
  }, [backToPricing, backToPricingHref, history]);
  const backToPricingLink = {
    backToPricingHref,
    onBackToPricing
  };
  if (tierOptionsStatus !== 'ready') {
    return Object.assign({}, backToPricingLink, {
      status: tierOptionsStatus
    });
  }
  if (tierOptions.length === 0) {
    return Object.assign({}, backToPricingLink, {
      status: 'unavailable'
    });
  }
  if (!catalog || !selectedTier || !selectedTierOption) {
    return Object.assign({}, backToPricingLink, {
      status: 'loading'
    });
  }
  return Object.assign({}, backToPricingLink, {
    status: 'ready',
    readyPage: {
      catalog,
      selectedTier,
      selectedTierOption,
      term,
      tierOptions,
      browserLocale,
      preferredCountryCode,
      currencyCode: displayedCurrencyCode
    },
    compareSeats
  });
}